export const APP_TIMEZONE = 'Asia/Kolkata';

const datePartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: APP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dateTimePartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: APP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function partsMap(parts) {
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

export function nowInAppTz(date = new Date()) {
  const parts = partsMap(dateTimePartsFormatter.formatToParts(date));
  return new Date(
  Number(parts.year),
  Number(parts.month) - 1,
  Number(parts.day),
  Number(parts.hour),
  Number(parts.minute),
  Number(parts.second ?? 0),
  );
}

export function todayString(date = new Date()) {
  const parts = partsMap(datePartsFormatter.formatToParts(date));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function yearMonthString(date = new Date()) {
  const parts = partsMap(datePartsFormatter.formatToParts(date));
  return `${parts.year}-${parts.month}`;
}

export function dateTimeLocalValue(date = new Date()) {
  const parts = partsMap(dateTimePartsFormatter.formatToParts(date));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function nowIsoTimestamp() {
  return new Date().toISOString();
}

export function parseApiDateOnly(value) {
  if (!value) return null;
  const datePart = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  return new Date(`${datePart}T12:00:00`);
}

export function toApiDateOnly(date) {
  if (!date) return '';
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return todayString(date);
  }
  return String(date).slice(0, 10);
}

export function addDaysToDateOnly(dateOnly, days) {
  const base = parseApiDateOnly(dateOnly);
  if (!base) return '';
  base.setDate(base.getDate() + days);
  return toApiDateOnly(base);
}

export function formatDate(value, options = {}) {
  if (!value) return '—';
  try {
    const date = value instanceof Date ? value : parseApiDateOnly(value) ?? new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(options.locale ?? 'en-IN', {
      timeZone: APP_TIMEZONE,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      ...options,
    });
  } catch {
    return String(value);
  }
}

export function formatDateTime(value, options = {}) {
  if (!value) return '—';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(options.locale ?? 'en-IN', {
      timeZone: APP_TIMEZONE,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  } catch {
    return String(value);
  }
}

export function formatTime(value, options = {}) {
  if (!value) return '—';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleTimeString(options.locale ?? 'en-IN', {
      timeZone: APP_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  } catch {
    return String(value);
  }
}

export function formatChatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = todayString();
  const msgDay = todayString(date);
  if (msgDay === today) {
    return formatTime(date);
  }
  return formatDate(date, { month: 'short', day: 'numeric' });
}

export function formatLastSeen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (todayString(date) === todayString()) return formatTime(date);
  return formatDate(date, { month: 'short', day: 'numeric' });
}

export function formatMonthYear(value, options = {}) {
  if (!value) return '—';
  try {
    const raw = String(value).trim();
    const date = /^\d{4}-\d{2}$/.test(raw)
      ? parseApiDateOnly(`${raw}-01`)
      : (value instanceof Date ? value : parseApiDateOnly(raw) ?? new Date(raw));
    if (!date || Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(options.locale ?? 'en-IN', {
      timeZone: APP_TIMEZONE,
      month: 'long',
      year: 'numeric',
      ...options,
    });
  } catch {
    return String(value);
  }
}

export function formatLocalDateInput(date = new Date()) {
  return todayString(date);
}

export function isValidAppDate(value) {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = parseApiDateOnly(v);
  return d !== null && !Number.isNaN(d.getTime()) && toApiDateOnly(d) === v;
}
